/**
 * The Command Desk's **UM Activity** panel — RULING R4″ (9 September 2026).
 *
 * Authority: **ruling R4″ (Alfred, the Technical Delegate and the product owner, 9 September
 * 2026, final — supersedes ruling R4′ of 11 August 2026)**, in his own words:
 *
 *   > "I will need to know whether the UMs are using this tools.. we can update the
 *   > requirements and change our promises if we need to.. I am not here to please them but
 *   > asking them to do their job properly. So TD should be able to see how many feedbacks
 *   > they have recorded, how many pending and how many released. And the last time they
 *   > recorded anything."
 *
 * Spec: `um-app/11-release-and-debrief.md` §4.1 and Q-11b, both amended in this run.
 * Route: `GET /td/um-activity` (`requireTd`), over migration 050's `app_um_note_activity()`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY IT LIVES ON THE COMMAND DESK RAIL
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The Command Desk is *"the only page the TD should need open before the first push-back"*
 * (control-centre spec 01 §1), and its right rail is already where the desk answers *"what must
 * I chase"* — Appointment Status, Alerts, Reports Outstanding. "Is anybody actually writing
 * feedback?" is the same kind of question and gets chased the same way, so it goes beside them
 * rather than onto a page of its own. It is **not** one of §9's eight rail panels and does not
 * claim to be; `DeskRail.tsx` still holds those eight in the order §2 fixes, and this arrives as
 * a ninth section under a ruling that postdates that specification.
 *
 * It reads its own route rather than being fed from `CommandDeskPage`'s composition, because it
 * is the one figure on the desk that is NOT derivable from another module's data — every other
 * panel is a view over fixtures, officials, appointments or report progress, and this one has a
 * primary read of its own.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT IT SHOWS, AND WHAT IT CANNOT SHOW
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Per manager: the name and country, `N Recorded · N Pending · N Released`, and
 * `Last Recorded: hh:mm` on the tournament clock (`Nothing Recorded Yet` when they have
 * recorded nothing at all).
 *
 * **There is no drill-down and there is nothing to drill into.** No row is a link, no row has a
 * disclosure, and the payload behind it carries no note id and no note text — so a click could
 * not open one if somebody wired it up. `Pending` is ONE number: drafts and locked notes are
 * added together, because R4″ does not distinguish them and *"she locked it"* is a statement
 * about somebody's decision to keep a thing private.
 *
 * The panel says so on its own face, in the foot line, for the reason the debrief footer prints
 * ruling R5's disclosure: a capability the people it measures have not been told about is the
 * failure mode, and the UM-facing copy was changed in the same run as this panel.
 */

import { fetchUmActivity } from "../../api/client.js";
import type { UmActivityRow } from "../../api/types.js";
import { FailBanner, Skeleton } from "../../app/states.js";
import { useAsync } from "../../app/useAsync.js";

/**
 * `18 Sep · 21:06` on the tournament clock (Asia/Tokyo — D7, spec 00 §7.3).
 *
 * The DAY is shown as well as the time, unlike `tokyoClock` in the UM app's sync chip: that one
 * describes something that happened minutes ago, and this one can legitimately point at
 * Wednesday. A bare `21:06` against a three-day-old capture would read as tonight, which is the
 * one thing this figure must not do.
 */
export function tokyoStamp(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Tokyo",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  })
    .format(parsed)
    .replace(",", " ·");
}

/**
 * One row's tally line, verbatim in one place so the panel and its test cannot come to say
 * different things. Plain Technical-Delegate language: three nouns, three numbers, no jargon.
 */
export function tallyLine(row: UmActivityRow): string {
  return `${String(row.recorded)} Recorded · ${String(row.pending)} Pending · ${String(row.released)} Released`;
}

/**
 * The last-recorded line. `Nothing Recorded Yet` is said in words rather than drawn as a dash,
 * because a dash in a column of times reads as a rendering failure and this state is a fact —
 * and it is the fact the ruling was asked for.
 */
export function lastRecordedLine(row: UmActivityRow): string {
  return row.lastActivityAt === null
    ? "Nothing Recorded Yet"
    : `Last Recorded: ${tokyoStamp(row.lastActivityAt)}`;
}

export function UmActivityPanel(): React.JSX.Element {
  const state = useAsync(() => fetchUmActivity(), []);

  return (
    <section className="panel panel--d4 umact" aria-labelledby="umActivityTitle">
      <div className="panel__head">
        <h3 className="panel__title" id="umActivityTitle">
          UM Activity
        </h3>
        <span className="panel__note">Feedback Recorded Per Umpires Manager</span>
        <span className="spacer" />
        <span className="kicker">Counts Only · Never What A Note Says</span>
      </div>

      {state.status === "loading" ? <Skeleton rows={4} /> : null}
      {state.status === "error" ? <FailBanner error={state.error} /> : null}

      {state.status === "ready" ? (
        state.data.rows.length === 0 ? (
          <div className="emptystate">
            <b>No Umpires Managers Have App Access</b>
            Nobody can record feedback until the Umpires Managers are invited in Configuration
            §04 App Access, so there is nothing to count yet.
          </div>
        ) : (
          <>
            <ul className="umact__list" data-testid="um-activity-rows">
              {state.data.rows.map((row) => (
                <li className="umact__row" key={row.umId} data-testid={`um-activity-${row.umId}`}>
                  <span className="umact__who">
                    <b>{row.mediaName}</b>
                    <i>{row.country}</i>
                  </span>
                  {/* `num` is the stylesheet's tabular-figures class, so six rows of counts
                      line up as a column rather than drifting with the digit widths. */}
                  <span className="umact__tally num">{tallyLine(row)}</span>
                  <span
                    className={
                      row.lastActivityAt === null ? "umact__last umact__last--none" : "umact__last"
                    }
                  >
                    {lastRecordedLine(row)}
                  </span>
                </li>
              ))}
            </ul>
            {/* The honest line, on the face of the thing rather than in a release note. It is
                the TD-side half of the disclosure the UM app now carries. */}
            <p className="umact__foot">
              Pending Is Drafts And Locked Notes Together. You Can See How Many And When — Never
              What Any Unreleased Note Says.
            </p>
          </>
        )
      ) : null}
    </section>
  );
}
